// FORM: https://github.com/modusign/modusign-client/blob/35ac05fa1f9337ff1aeacd765766a3b36653b583/app/src/components/TextField/ConfigurableTextField/index.tsx

import { Box } from '@modusign/design-system';
import { useTextField } from '@modusign/react-signing';
import { cx } from 'emotion';
import { useMemo, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle, useUpdateEffect } from 'react-use';

import RoundedXButton from '@app/components/Buttons/RoundedXButton';
import { FieldBase } from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import {
  toAbsolutePoint,
  toAbsoluteSize,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { clampFieldPoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/clampFieldPoint';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import { getTextBaseFieldCorrectedSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getTextBaseFieldCorrectedSize';
import { getTextBaseFieldMinimumSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getTextBaseFieldMinimumSize';
import {
  AbsolutePoint,
  AbsoluteSize,
  ReactSigningFieldRendererProps,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import TextFieldPlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/Placeholders/TextFieldPlaceHolder';
import { TextFieldContextMenu } from '@app/features/apply-react-signing/components/FieldSetupStep/TextFieldContextMenu';
import { useOptimalFieldsContext } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';
import { useTransformTextStyle } from '@app/features/apply-react-signing/libs/hooks/useTransFormTextStyle';
import createFieldClassNameByParticipantOrder from '@app/libs/participantFields/createFieldClassNameByParticipantOrder';
import PDFFontNormalizer from '@app/libs/reactPdfJs/utils/PDFFontNormalizer';

import './ConfigurableTextField.scss';

export function ConfigurableTextFieldRenderer({
  restrictModification,
  readOnly,
  fieldId,
  scale,
  viewport,
  isDataLabelEditable,
}: ReactSigningFieldRendererProps) {
  const [isResizing, setResizing] = useState(false);
  const [showContextMenu, toggleContextMenu] = useToggle(true);
  const [showGuideline, setShowGuideline] = useState(false);
  const {
    field,
    setPosition,
    setSize,
    setDataLabel,
    setAlign,
    setFontSize,
    setFontStyle,
    setRequired,
    setFormat,
    setValueDescription,
  } = useTextField(fieldId);

  const [isOpenFormatMenu, setIsOpenFormatMenu] = useState(false);

  const [isValueDescriptionCollapsed, setIsValueDescriptionCollapsed] = useState(
    !field?.config.valueDescription
  );

  const fontSize = field?.config.fontStyle.size ?? 60;
  const fontPixelSize: number = PDFFontNormalizer.toPixelFontSize({ fontSize }, scale);

  const {
    evaluated: { isDataLabelDuplicated },
    removeField,
  } = useOptimalFieldsContext<'TEXT', 'SIGNER'>();

  const absoluteSize = toAbsoluteSize(field?.size ?? { width: 0, height: 0 }, viewport);
  const absolutePoint = toAbsolutePoint(field?.position ?? { x: 0, y: 0 }, viewport);

  // 'g', 'j', 'q' 등과 같이 폰트 높이 아래로 튀어 나오는 영역을 보정하기 위한 값
  const minSize = getTextBaseFieldMinimumSize(fontSize, scale);

  const { transformTextStyle } = useTransformTextStyle({
    fontFamily: field?.config.fontStyle.type ?? 'NOTO_SANS',
    textAlign: field?.config.textAlign ?? 'LEFT',
    fontSize: field?.config.fontStyle.size ?? 12,
    size: absoluteSize,
    scale,
  });

  const dataLabelErrorMessage = useMemo(() => {
    if (!field?.dataLabel.trim()) {
      return '데이터 라벨 입력은 필수입니다.';
    }

    if (isDataLabelDuplicated) {
      return '중복된 데이터 라벨입니다.';
    }

    return '';
  }, [field?.dataLabel, isDataLabelDuplicated]);

  const correctSize = () => {
    const relativeSize = toRelativeSize(
      getTextBaseFieldCorrectedSize({
        size: absoluteSize,
        scale,
        fontSize: field?.config.fontStyle.size ?? 60,
      }),
      viewport
    );
    setSize(relativeSize.width, relativeSize.height);
  };

  // 폰트크기가 변경되었을 때, 최소 크기와 글줄 길이에 맞게 보정합니다.
  useUpdateEffect(() => {
    correctSize();
  }, [fontPixelSize]);

  if (!field || field.participantReference.type !== 'SIGNER') {
    return null;
  }

  const handlePointChangeStart = () => {
    setShowGuideline(true);
    toggleContextMenu(true);
  };

  const handlePointChangeStop = () => {
    setShowGuideline(false);
  };

  const handlePointChange = (point: AbsolutePoint) => {
    const relativePoint = clampFieldPoint(toRelativePoint(point, viewport));
    setPosition(relativePoint.x, relativePoint.y);
    setShowGuideline(true);
  };

  const handleChangeSizeStart = () => {
    setResizing(true);
    setShowGuideline(true);
  };

  const handleChangeSize = (size: AbsoluteSize) => {
    const relativeSize = toRelativeSize(size, viewport);

    setShowGuideline(true);
    setSize(relativeSize.width, relativeSize.height);
  };

  const handleChangeSizeStop = () => {
    setResizing(false);
    setShowGuideline(false);
    correctSize();
  };

  const handleRemoveField = () => {
    removeField(fieldId);
  };

  const participantOrder = field.participantReference.index + 1;

  const configurableTextFieldClassByOrder = createFieldClassNameByParticipantOrder(
    'ConfigurableTextField',
    participantOrder
  );

  const handleChangeFontSize = (fontSize: number) => {
    setFontSize(fontSize);
  };

  return (
    <FieldBase
      className={cx({
        ConfigurableTextField: true,
        'ConfigurableTextField--resizing': isResizing,
        [configurableTextFieldClassByOrder]: true,
        'ConfigurableTextField__disable-resize':
          /**
           * format 메뉴가 열려있는 상태에서 리사이즈 handler가 메뉴보다 위에 있는 상황이 생겨 disable해서 handler 제거해줍니다.
           * 해당 문제를 근본적으로 해결하려면 FieldBase의 DOM구조를 handler 보다 contextMenu가 위에 있도록 변경해야합니다.
           */
          readOnly || restrictModification || isOpenFormatMenu,
      })}
      data-testid="text-field"
      dragCancelSelectors={['.ConfigurableTextField__context-menu']}
      fieldGuideline={{
        guideLineOffset: {
          left: 2,
          top: 2,
          right: 1,
          bottom: 1,
        },
        viewport,
        showGuideLine: showGuideline,
      }}
      maxConstraints={[viewport.width - absolutePoint.x, viewport.height - absolutePoint.y]}
      minConstraints={[minSize.width, minSize.height]}
      point={absolutePoint}
      readOnly={readOnly}
      restrictModification={restrictModification}
      size={absoluteSize}
      snapSize={createAbsoluteSize({
        width: 1,
        height: fontPixelSize,
      })}
      onPointChange={handlePointChange}
      onPointChangeStart={handlePointChangeStart}
      onPointChangeStop={handlePointChangeStop}
      onSizeChange={handleChangeSize}
      onSizeChangeStart={handleChangeSizeStart}
      onSizeChangeStop={handleChangeSizeStop}
    >
      <Box height="100%" width="100%">
        <OutsideClickHandler
          onOutsideClick={() => {
            setIsOpenFormatMenu(false);
            if (field.dataLabel && !isDataLabelDuplicated) {
              toggleContextMenu(false);
            }
          }}
        >
          <Box height="100%" position="absolute" width="100%">
            <TextFieldPlaceholder
              fontPixelSize={fontPixelSize}
              format={field.config.format}
              participantOrder={participantOrder}
              star={field.config.required}
              style={transformTextStyle}
            />
            {!readOnly && !restrictModification && (
              <Box className="ConfigurableTextField__x-button">
                <RoundedXButton onClick={handleRemoveField} />
              </Box>
            )}
          </Box>
          {showContextMenu && !readOnly && !restrictModification && (
            <Box className="ConfigurableTextField__context-menu">
              <TextFieldContextMenu
                dataLabelErrorMessage={dataLabelErrorMessage}
                field={field}
                isDataLabelEditable={isDataLabelEditable}
                isOpenFormatMenu={isOpenFormatMenu}
                isValueDescriptionCollapsed={isValueDescriptionCollapsed}
                participantOrder={participantOrder}
                onDataLabelChange={setDataLabel}
                onFontFamilyChange={setFontStyle}
                onFontSizeChange={handleChangeFontSize}
                onFormatChange={setFormat}
                onIsOpenFormatMenuChange={setIsOpenFormatMenu}
                onRequiredChange={setRequired}
                onTextAlignChange={setAlign}
                onValueDescriptionChanged={setValueDescription}
                onValueDescriptionCollapseMenuClick={() =>
                  setIsValueDescriptionCollapsed(!isValueDescriptionCollapsed)
                }
              />
            </Box>
          )}
        </OutsideClickHandler>
      </Box>
    </FieldBase>
  );
}
